import axios from 'axios';
import {
  getLocalStorage,
  getSessionStorage,
  safeClearStorage,
  safeGetStorageItem,
  safeSetStorageItem,
} from './browserStorage';
import { clearBrowserCaches } from './browserStorageCleanup';

const VERSION_KEY = 'app_version';
const STORAGE_CLEANUP_KEY = 'otello_storage_cleanup_version';
const STORAGE_CLEANUP_VERSION = '2026-05-05-browser-storage-v1';
const PRESERVED_KEYS = ['whats_new_seen', 'color_scheme', 'theme_palette'];

/**
 * Forza il logout chiamando l'API di logout e pulendo gli storage.
 */
const getDefaultWindow = () => (typeof window !== 'undefined' ? window : null);

const redirectToLogin = (windowObject = getDefaultWindow()) => {
  try {
    if (windowObject?.location) {
      windowObject.location.href = '/app/login';
      return true;
    }
  } catch (_) {
    return false;
  }

  return false;
};

export async function forceLogout(
  newVersion,
  {
    axiosClient = axios,
    clearCaches = clearBrowserCaches,
    localStorageObject = getLocalStorage(),
    sessionStorageObject = getSessionStorage(),
    windowObject = getDefaultWindow(),
  } = {}
) {
  try {
    await axiosClient.delete('/auth/sign_out');
  } catch (error) {
    // Ignora errori (potrebbe già essere sloggato)
  }

  const preservedValues = {};
  PRESERVED_KEYS.forEach((key) => {
    const value = safeGetStorageItem(localStorageObject, key);
    if (value !== null) {
      preservedValues[key] = value;
    }
  });

  await clearCaches({ includeCurrentAuth: true });

  safeClearStorage(localStorageObject);
  safeClearStorage(sessionStorageObject);

  Object.entries(preservedValues).forEach(([key, value]) => {
    safeSetStorageItem(localStorageObject, key, value);
  });

  safeSetStorageItem(localStorageObject, VERSION_KEY, newVersion);
  safeSetStorageItem(
    localStorageObject,
    STORAGE_CLEANUP_KEY,
    STORAGE_CLEANUP_VERSION
  );

  redirectToLogin(windowObject);
}

/**
 * Controlla se c'è una nuova versione dell'app (chiamato solo al boot).
 * Una release aggiorna il riferimento locale senza invalidare la sessione.
 */
export async function checkAppVersion({
  clearCaches = clearBrowserCaches,
  fetchClient = typeof fetch !== 'undefined' ? fetch : null,
  localStorageObject = getLocalStorage(),
} = {}) {
  try {
    if (typeof fetchClient !== 'function') {
      return false;
    }

    const response = await fetchClient(`/version.json?t=${Date.now()}`);
    if (!response.ok) {
      return false;
    }

    const { version } = await response.json();
    if (!version) return false;

    const storedVersion = safeGetStorageItem(localStorageObject, VERSION_KEY);
    const cleanupVersion = safeGetStorageItem(
      localStorageObject,
      STORAGE_CLEANUP_KEY
    );

    if (storedVersion !== version) {
      safeSetStorageItem(localStorageObject, VERSION_KEY, version);
    }

    if (cleanupVersion !== STORAGE_CLEANUP_VERSION) {
      await clearCaches({ includeCurrentAuth: false });
      safeSetStorageItem(
        localStorageObject,
        STORAGE_CLEANUP_KEY,
        STORAGE_CLEANUP_VERSION
      );
      safeSetStorageItem(localStorageObject, VERSION_KEY, version);
    }

    return false;
  } catch (error) {
    return false;
  }
}

export default {
  checkAppVersion,
  forceLogout,
};
